import React, { useState } from 'react';
import styles from './Dashboard.module.css';
import Overlay from './Overlay';

function fmt(n) { return `$${Number(n).toFixed(2)}`; }
function fmtDate(d) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : d.toISOString();
  if (s.length === 10) return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getFullDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
}

export default function Dashboard({ clients, transactions, config, refresh, showToast, onNavigate, API, authHeader }) {

  const [showQuickLoad, setShowQuickLoad] = useState(false);
  const [quickClient,   setQuickClient]   = useState('');
  const [quickPkg,      setQuickPkg]      = useState('');
  const [quickCustom,   setQuickCustom]   = useState('');
  const [loadingQuick,  setLoadingQuick]  = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient,     setNewClient]     = useState({ name: '', email: '', phone: '', athlete: '' });
  const [addingClient,  setAddingClient]  = useState(false);

  const recentSessions = transactions
    .filter(t => t.type === 'session_debit')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const recentCredits = transactions
    .filter(t => t.type === 'credit_load')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const topClients = [...clients]
    .sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance))
    .slice(0, 5);

  const totalBalance = clients.reduce((s, c) => s + parseFloat(c.balance || 0), 0);
  const lowCount     = clients.filter(c => parseFloat(c.balance) < 75).length;
  const balStatus    = (b) => b <= 0 ? 'empty' : b < 75 ? 'low' : b < 150 ? 'ok' : 'good';

  const sessionLabel = (type) =>
    config?.sessionRates?.[type]?.label || type;

  const handleQuickLoad = async () => {
    if (!quickClient) { showToast('Please select a client', 'error'); return; }
    if (!quickPkg && !quickCustom) { showToast('Please select a package or enter an amount', 'error'); return; }
    setLoadingQuick(true);
    try {
      const body = quickCustom
        ? { customAmount: parseFloat(quickCustom) }
        : { packageId: quickPkg };
      const res = await fetch(`${API}/clients/${quickClient}/load-credit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body)
      });
      if (!res.ok) { const e = await res.json(); showToast(e.error, 'error'); return; }
      const data = await res.json();
      const name = clients.find(c => c.id === quickClient)?.name || 'Client';
      showToast(`${fmt(data.transaction.amount)} loaded to ${name}'s account`);
      setShowQuickLoad(false);
      setQuickClient(''); setQuickPkg(''); setQuickCustom('');
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setLoadingQuick(false); }
  };

  const handleAddClient = async () => {
    if (!newClient.name || !newClient.email) { showToast('Name and email required', 'error'); return; }
    setAddingClient(true);
    try {
      const res = await fetch(`${API}/clients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(newClient)
      });
      if (!res.ok) { const e = await res.json(); showToast(e.error, 'error'); return; }
      showToast(`${newClient.name} added successfully`);
      setShowAddClient(false);
      setNewClient({ name: '', email: '', phone: '', athlete: '' });
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setAddingClient(false); }
  };

  return (
    <div className={styles.dashboard}>

      {/* ── Greeting header ──────────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.greeting}>{getGreeting()}, Michael</div>
          <div className={styles.dateStr}>{getFullDate()}</div>
        </div>
        <div className={styles.headerStats}>
          <div className={styles.statChip}>
            <span className={styles.statIcon}>◎</span>
            <span className={styles.statVal}>{clients.length}</span>
            <span className={styles.statLbl}>Clients</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statChip}>
            <span className={styles.statIcon}>$</span>
            <span className={styles.statVal}>{fmt(totalBalance)}</span>
            <span className={styles.statLbl}>Total Balance</span>
          </div>
          {lowCount > 0 && (
            <>
              <div className={styles.statDivider} />
              <div className={`${styles.statChip} ${styles.statWarn}`}>
                <span className={styles.statIcon}>⚠</span>
                <span className={styles.statVal}>{lowCount}</span>
                <span className={styles.statLbl}>Low Balance</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Three panels ─────────────────────────────────────────────────── */}
      <div className={styles.panels}>

        {/* Panel 1: Client Accounts */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Client Accounts</h2>
            <button className={styles.viewAll} onClick={() => onNavigate('clients')}>View All →</button>
          </div>
          <div className={styles.panelBody}>
            {topClients.length === 0 && <p className={styles.empty}>No clients yet.</p>}
            {topClients.map(c => (
              <button key={c.id} className={styles.clientRow} onClick={() => onNavigate('clients', c.id)}>
                <div className={styles.clientAvatar}>{c.name.charAt(0)}</div>
                <div className={styles.clientInfo}>
                  <div className={styles.clientName}>{c.name}</div>
                  <div className={styles.clientSub}>
                    {c.athletes?.filter(a => a.name).map(a => a.name).join(', ') || c.athlete || 'No athlete'}
                  </div>
                </div>
                <div className={`${styles.clientBal} ${styles[`bal_${balStatus(c.balance)}`]}`}>
                  {fmt(c.balance)}
                </div>
              </button>
            ))}
          </div>
          <div className={styles.panelFooter}>
            <button className={styles.actionBtn} onClick={() => setShowAddClient(true)}>
              <span>＋</span> Add New Client
            </button>
          </div>
        </div>

        {/* Panel 2: Recent Sessions */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Recent Sessions</h2>
            <button className={styles.viewAll} onClick={() => onNavigate('transactions')}>View All →</button>
          </div>
          <div className={styles.panelBody}>
            {recentSessions.length === 0 && <p className={styles.empty}>No sessions yet.</p>}
            {recentSessions.map(tx => (
              <div key={tx.id} className={styles.txRow}>
                <div className={styles.txLeft}>
                  <span className="tag tag-orange">
                    {sessionLabel(tx.sessionType)}
                  </span>
                  <div>
                    <div className={styles.txName}>{tx.clientName}</div>
                    <div className={styles.txDate}>{fmtDate(tx.sessionDate || tx.createdAt)}</div>
                  </div>
                </div>
                <div className={styles.txAmount}>{fmt(tx.amount)}</div>
              </div>
            ))}
          </div>
          <div className={styles.panelFooter}>
            <button className={styles.actionBtn} onClick={() => onNavigate('charge')}>
              <span>⚡</span> Charge a Session
            </button>
          </div>
        </div>

        {/* Panel 3: Recent Credit Loads */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Recent Credit Loads</h2>
            <button className={styles.viewAll} onClick={() => onNavigate('transactions')}>View All →</button>
          </div>
          <div className={styles.panelBody}>
            {recentCredits.length === 0 && <p className={styles.empty}>No credit loads yet.</p>}
            {recentCredits.map(tx => (
              <div key={tx.id} className={styles.txRow}>
                <div className={styles.txLeft}>
                  <span className="tag tag-green">Credit</span>
                  <div>
                    <div className={styles.txName}>{tx.clientName}</div>
                    <div className={styles.txDate}>{fmtDate(tx.createdAt)}</div>
                  </div>
                </div>
                <div className={`${styles.txAmount} ${styles.pos}`}>+{fmt(tx.amount)}</div>
              </div>
            ))}
          </div>
          <div className={styles.panelFooter}>
            <button className={styles.actionBtn} onClick={() => setShowQuickLoad(true)}>
              <span>＄</span> Load Account Credits
            </button>
          </div>
        </div>

      </div>

      {/* ── Session rates bar ─────────────────────────────────────────────── */}
      {config && (
        <div className={styles.ratesBar}>
          <span className={styles.ratesLabel}>Session Rates:</span>
          {Object.entries(config.sessionRates).map(([key, r]) => (
            <span key={key} className={styles.rateChip}>
              {r.icon} {r.label} — <strong>{fmt(r.rate)}</strong>
            </span>
          ))}
          <button className={styles.ratesEditBtn} onClick={() => onNavigate('sessiontypes')}>
            ✎ Edit
          </button>
        </div>
      )}

      {/* ── Quick Load Credits Modal ──────────────────────────────────────── */}
      {showQuickLoad && config && (
        <Overlay className={styles.overlay} onClose={() => setShowQuickLoad(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Load Account Credits</h3>
              <button className={styles.modalClose} onClick={() => setShowQuickLoad(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Select Client</label>
                <select className={styles.formSelect} value={quickClient}
                  onChange={e => setQuickClient(e.target.value)}>
                  <option value="">— Choose a client —</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} (Balance: {fmt(c.balance)})
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.pkgGrid}>
                {config.creditPackages.map(pkg => (
                  <button key={pkg.id}
                    className={`${styles.pkgCard} ${quickPkg === pkg.id ? styles.pkgSelected : ''}`}
                    onClick={() => { setQuickPkg(pkg.id); setQuickCustom(''); }}>
                    <div className={styles.pkgLabel}>{pkg.label}</div>
                    <div className={styles.pkgAmount}>{fmt(pkg.amount)}</div>
                  </button>
                ))}
              </div>
              <div className={styles.orDivider}><span>or custom amount</span></div>
              <input className={styles.formInput} type="number"
                placeholder="Enter amount (e.g. 200)"
                value={quickCustom}
                onChange={e => { setQuickCustom(e.target.value); setQuickPkg(''); }} />
              <button className={styles.confirmBtn}
                disabled={loadingQuick || !quickClient || (!quickPkg && !quickCustom)}
                onClick={handleQuickLoad}>
                {loadingQuick ? 'Processing...' : `Load ${quickPkg
                  ? fmt(config.creditPackages.find(p => p.id === quickPkg)?.amount || 0)
                  : quickCustom ? `$${quickCustom}` : 'Credits'}`}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Add New Client Modal ──────────────────────────────────────────── */}
      {showAddClient && (
        <Overlay className={styles.overlay} onClose={() => setShowAddClient(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Add New Client</h3>
              <button className={styles.modalClose} onClick={() => setShowAddClient(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              {[
                { key: 'name',    label: 'Parent Name *', placeholder: 'e.g. Sarah Johnson', type: 'text' },
                { key: 'email',   label: 'Email *',        placeholder: 'parent@email.com',   type: 'email' },
                { key: 'phone',   label: 'Phone',          placeholder: '(720) 555-0000',     type: 'tel' },
                { key: 'athlete', label: 'Athlete Name',   placeholder: "Athlete's first name", type: 'text' },
              ].map(f => (
                <div key={f.key} className={styles.formField}>
                  <label className={styles.formLabel}>{f.label}</label>
                  <input type={f.type} className={styles.formInput} placeholder={f.placeholder}
                    value={newClient[f.key]}
                    onChange={e => setNewClient(p => ({ ...p, [f.key]: e.target.value }))} />
                </div>
              ))}
              <p className={styles.formNote}>Full athlete details can be added from the Client Accounts page.</p>
              <button className={styles.confirmBtn} disabled={addingClient} onClick={handleAddClient}>
                {addingClient ? 'Adding...' : 'Add Client'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

    </div>
  );
}
